import React from 'react';
import { clsx } from 'clsx';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helper?: string;
  variant?: 'default' | 'elegant';
  inputSize?: 'sm' | 'md' | 'lg';
}

export const Input: React.FC<InputProps> = ({ 
  label, 
  error, 
  helper,
  variant = 'default',
  inputSize = 'md',
  className,
  id,
  ...props 
}) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  const variants = {
    default: 'border-neutral-300 bg-white focus:border-primary-500 focus:ring-primary-500',
    elegant: 'input-elegant border-neutral-200 focus:border-primary-400 focus:ring-primary-400/20'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-sm',
    lg: 'px-5 py-4 text-base'
  };

  return (
    <div className={clsx("space-y-2 w-full", className)}>
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-semibold text-neutral-900"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={clsx(
          "w-full px-4 py-2 bg-white border border-neutral-300 rounded-lg shadow-sm",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
          "transition duration-150 ease-in-out",
          "disabled:bg-neutral-100 disabled:cursor-not-allowed",
          variants[variant],
          sizes[inputSize],
          error && 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/50',
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 font-medium flex items-center space-x-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </p>
      )}
      {helper && !error && (
        <p className="text-sm text-neutral-600">{helper}</p>
      )}
    </div>
  );
};